import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { AuthUserContext } from "../../context/AuthUserContext";
import { useTranslation } from "react-i18next";
import {
  SectionCard,
  SectionHeader,
  SectionTitle,
  SectionBody,
  StepBadge,
  StepBadgeContent,
} from "../checkout/CheckoutSection";

const LocationButton = styled.button`
  border-radius: var(--border-radius-large);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) var(--spacing-lg);
  margin: 0 0 var(--spacing-lg) 0;
  background-color: var(--primary-300);
  color: var(--primary-100);
  border: none;
  width: 100%;
  transition: background-color 0.15s ease-in-out, color 0.15s ease-in-out;

  @media (min-width: 768px) {
    width: auto;
  }

  &:hover {
    background-color: var(--primary-200);
    color: var(--bg-100);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LocationError = styled.p`
  color: var(--error-color);
  font-size: var(--font-size-small);
  margin: calc(var(--spacing-md) * -1) 0 var(--spacing-lg) 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  @media (min-width: 767px) {
    flex-direction: row;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  flex: 1;
  min-width: 0;
`;

const Label = styled.label`
  font-size: var(--font-size-small);
  color: var(--text-200);
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid transparent;
  border-radius: var(--border-radius-base);
  background-color: var(--bg-200);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out,
    background-color 0.15s ease-in-out;
  &:disabled {
    background-color: var(--bg-200);
    opacity: 0.6;
    cursor: not-allowed;
  }
  &:focus {
    outline: none;
    background-color: var(--bg-100);
    border-color: var(--primary-200);
    box-shadow: 0 0 0 3px var(--primary-300);
  }
`;

const TypeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
`;

const TypeLabel = styled.p`
  font-size: var(--font-size-base);
  color: var(--text-100);
  margin: 0;
  text-transform: uppercase;
`;

const TypeChip = styled.button`
  padding: var(--spacing-xs) var(--spacing-lg);
  border-radius: var(--border-radius-large);
  text-transform: capitalize;
  border: 1px solid
    ${({ $isActive }) => ($isActive ? "var(--primary-200)" : "var(--bg-300)")};
  background-color: ${({ $isActive }) =>
    $isActive ? "var(--primary-200)" : "var(--bg-100)"};
  color: ${({ $isActive }) => ($isActive ? "var(--bg-100)" : "var(--text-200)")};
  &:hover {
    border-color: var(--primary-200);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding-top: var(--spacing-sm);
`;

const SaveButton = styled.button`
  border-radius: var(--border-radius-large);
  background-color: var(--primary-200);
  border: none;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-xl);
  &:hover {
    background-color: var(--primary-100);
  }

  @media (min-width: 767px) {
    width: auto;
  }
`;

const CancelButton = styled.button`
  border-radius: var(--border-radius-large);
  background-color: var(--bg-200);
  border: none;
  color: var(--text-200);
  padding: var(--spacing-sm) var(--spacing-xl);
  &:hover {
    color: var(--primary-200);
    background-color: var(--bg-200);
  }
`;

const AddressBookCheckout = ({
  editAddress,
  showAddressForm,
  setShowAddressForm = () => {},
}) => {
  const { t, i18n } = useTranslation();
  const [locating, setLocating] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [street, setStreet] = useState("");
  const [streetNumber, setStreetNumber] = useState("");
  const [secondaryStreet, setSecondaryStreet] = useState("");
  const [buildingNumber, setBuildingNumber] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  const [type, setType] = useState("Home");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const { userProfile, updateAddressBook, createAddressBook, updateUserInfo } =
    useContext(AuthUserContext);

  const hasExistingAddresses = (userProfile?.addresses?.length || 0) > 0;

  useEffect(() => {
    if (userProfile && userProfile.phone_number && !editAddress) {
      setPhoneNumber(userProfile.phone_number);
    }
  }, [userProfile]);

  useEffect(() => {
    if (userProfile && !editAddress) {
      setFirstName(userProfile.first_name || "");
      setLastName(userProfile.last_name || "");
    }
  }, [userProfile]);

  const handleCancelClick = () => {
    setShowAddressForm(!showAddressForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      firstName !== userProfile.first_name ||
      lastName !== userProfile.last_name
    ) {
      try {
        await updateUserInfo({ first_name: firstName, last_name: lastName });
      } catch (error) {
        return;
      }
    }

    const addressData = {
      country,
      city,
      postal_code: postalCode,
      street,
      street_number: streetNumber,
      secondary_street: secondaryStreet || null,
      building_number: buildingNumber || null,
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      is_primary: true,
      phone_number: phoneNumber,
      type: type,
    };

    try {
      if (!editAddress) {
        await createAddressBook(addressData);
      } else {
        await updateAddressBook(editAddress.id, addressData);
      }
      handleCancelClick();
    } catch (error) {}
  };

  useEffect(() => {
    if (editAddress) {
      setCountry(editAddress.country || "");
      setCity(editAddress.city || "");
      setPostalCode(editAddress.postal_code || "");
      setStreet(editAddress.street || "");
      setStreetNumber(editAddress.street_number || "");
      setSecondaryStreet(editAddress.secondary_street || "");
      setBuildingNumber(editAddress.building_number || "");
      setType(editAddress.type);
      setPhoneNumber(editAddress.phone_number || "");
    } else {
      setCountry("");
      setCity("");
      setPostalCode("");
      setStreet("");
      setStreetNumber("");
      setSecondaryStreet("");
      setBuildingNumber("");
      setType("Home");
    }
  }, [editAddress]);

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(t("ADDRESS_BOOK.LOCATION_ERROR"));
      return;
    }
    setLocationError("");
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
            { headers: { "Accept-Language": i18n.language } }
          );
          const data = await response.json();
          const addr = data.address || {};
          setCountry(addr.country || "");
          setCity(addr.city || addr.town || addr.village || addr.municipality || "");
          setPostalCode(addr.postcode || "");
          setStreet(addr.road || "");
          setStreetNumber(addr.house_number || "");
          setLatitude(lat);
          setLongitude(lng);
        } catch (error) {
          setLocationError(t("ADDRESS_BOOK.LOCATION_ERROR"));
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocationError(t("ADDRESS_BOOK.LOCATION_PERMISSION_DENIED"));
        setLocating(false);
      }
    );
  };

  return (
    <SectionCard>
      <SectionHeader>
        <StepBadge>
          <StepBadgeContent step={1} />
        </StepBadge>
        <SectionTitle>
          {editAddress
            ? t("ADDRESS_BOOK.EDIT_ADDRESS")
            : t("ADDRESS_BOOK.ADD_NEW_ADDRESS")}
        </SectionTitle>
      </SectionHeader>
      <SectionBody>
        <LocationButton
          type="button"
          onClick={handleUseCurrentLocation}
          disabled={locating}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_326_5458_checkout)">
              <path
                d="M9.2124 12.5C9.2124 13.2956 9.52847 14.0587 10.0911 14.6213C10.6537 15.1839 11.4168 15.5 12.2124 15.5C13.0081 15.5 13.7711 15.1839 14.3337 14.6213C14.8963 14.0587 15.2124 13.2956 15.2124 12.5C15.2124 11.7044 14.8963 10.9413 14.3337 10.3787C13.7711 9.81607 13.0081 9.5 12.2124 9.5C11.4168 9.5 10.6537 9.81607 10.0911 10.3787C9.52847 10.9413 9.2124 11.7044 9.2124 12.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.2124 12.5C4.2124 14.6217 5.05526 16.6566 6.55555 18.1569C8.05584 19.6571 10.0907 20.5 12.2124 20.5C14.3341 20.5 16.369 19.6571 17.8693 18.1569C19.3695 16.6566 20.2124 14.6217 20.2124 12.5C20.2124 10.3783 19.3695 8.34344 17.8693 6.84315C16.369 5.34285 14.3341 4.5 12.2124 4.5C10.0907 4.5 8.05584 5.34285 6.55555 6.84315C5.05526 8.34344 4.2124 10.3783 4.2124 12.5Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.2124 2.5V4.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.2124 20.5V22.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M20.2124 12.5H22.2124"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.2124 12.5H4.2124"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_326_5458_checkout">
                <rect
                  width="24"
                  height="24"
                  fill="white"
                  transform="translate(0.212402 0.5)"
                />
              </clipPath>
            </defs>
          </svg>
          {locating
            ? t("ADDRESS_BOOK.DETECTING_LOCATION")
            : t("ADDRESS_BOOK.USE_MY_CURRENT_LOCATION")}
        </LocationButton>
        {locationError && <LocationError>{locationError}</LocationError>}
        <Form onSubmit={handleSubmit}>
          <FormRow>
            <InputGroup>
              <Label>{t("LABELS.NAME")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.NAME")}
                aria-label={t("LABELS.NAME")}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("LABELS.SURNAME")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.SURNAME")}
                aria-label={t("LABELS.SURNAME")}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </InputGroup>
          </FormRow>

          <FormRow>
            <InputGroup>
              <Label>{t("LABELS.PHONE_NUMBER")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.PHONE_NUMBER")}
                aria-label={t("LABELS.PHONE_NUMBER")}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("LABELS.COUNTRY")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.COUNTRY")}
                aria-label={t("LABELS.COUNTRY")}
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              />
            </InputGroup>
          </FormRow>
          <FormRow>
            <InputGroup>
              <Label>{t("LABELS.POSTAL_CODE")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.POSTAL_CODE")}
                aria-label={t("LABELS.POSTAL_CODE")}
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("LABELS.CITY")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.CITY")}
                aria-label={t("LABELS.CITY")}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </InputGroup>
          </FormRow>
          <FormRow>
            <InputGroup>
              <Label>{t("LABELS.STREET")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.STREET")}
                aria-label={t("LABELS.STREET")}
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                required
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("LABELS.STREET_NUMBER")}</Label>
              <Input
                type="text"
                placeholder={t("LABELS.STREET_NUMBER")}
                aria-label={t("LABELS.STREET_NUMBER")}
                value={streetNumber}
                onChange={(e) => setStreetNumber(e.target.value)}
                required
              />
            </InputGroup>
          </FormRow>

          <FormRow>
            <InputGroup>
              <Label>{t("LABELS.SECONDARY_STREET")}</Label>
              <Input
                type="text"
                placeholder={`${t("LABELS.SECONDARY_STREET")}${t(
                  "LABELS.OPTIONAL"
                )}`}
                aria-label={t("LABELS.SECONDARY_STREET")}
                value={secondaryStreet}
                onChange={(e) => setSecondaryStreet(e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <Label>{t("LABELS.BUILDING_NUMBER")}</Label>
              <Input
                type="text"
                placeholder={`${t("LABELS.BUILDING_NUMBER")}${t(
                  "LABELS.OPTIONAL"
                )}`}
                aria-label={t("LABELS.BUILDING_NUMBER")}
                value={buildingNumber}
                onChange={(e) => setBuildingNumber(e.target.value)}
              />
            </InputGroup>
          </FormRow>
          <TypeGroup>
            <TypeLabel>{t("ADDRESS_BOOK.TYPE")}</TypeLabel>
            <TypeChip
              type="button"
              $isActive={type === "Home"}
              onClick={() => setType("Home")}
            >
              {t("ADDRESS_BOOK.HOME")}
            </TypeChip>
            <TypeChip
              type="button"
              $isActive={type === "Work"}
              onClick={() => setType("Work")}
            >
              {t("ADDRESS_BOOK.WORK")}
            </TypeChip>
            <TypeChip
              type="button"
              $isActive={type === "Other"}
              onClick={() => setType("Other")}
            >
              {t("ADDRESS_BOOK.OTHER")}
            </TypeChip>
          </TypeGroup>

          <Actions>
            <SaveButton type="submit">
              {t("BUTTONS.SAVE_AND_DELIVER_HERE")}
            </SaveButton>
            {hasExistingAddresses && (
              <CancelButton type="button" onClick={handleCancelClick}>
                {t("BUTTONS.CANCEL")}
              </CancelButton>
            )}
          </Actions>
        </Form>
      </SectionBody>
    </SectionCard>
  );
};

export default AddressBookCheckout;
